import { FC, useEffect, useState } from "react";
import { FormContainerStateReturnType, useTrans } from "../../../hooks";
import { useAuthData } from "../../../contexts";
import {
    VStack,
    Input,
    useToast,
    Stack,
    Text,
    Box,
    SimpleGrid,
    Flex,
    Avatar,
    Checkbox,
} from "@chakra-ui/react";
import {
    CoordinatorRequest as Entity,
    CoordinatorRequestApi as EntityApi,
    UserApi,
    User,
    CompanyApi,
} from "../../../apis";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { partnershipCompanySchema } from "./partnershipCompanySchema";
import { AppFormContainer } from "../../../containers";
import { AppFormControl } from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";
import { APPC } from "../../../config";

export const PartnershipCompanyForm: FC<{
    formContainerState: FormContainerStateReturnType;
    data;
}> = ({ formContainerState, data }) => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);
    const { user } = useAuthData();

    // state & const
    const [loading, setLoading] = useState(true);
    const [existinginternshipCoordinator, setExistinginternshipCoordinator] =
        useState<User[]>([]);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const USERTYPE = APPC.BE.User.USERTYPE;

    const {
        handleSubmit,
        register,
        reset,
        formState: { errors },
    } = useForm<Entity>({
        resolver: yupResolver(partnershipCompanySchema(t, isFormOpen)),
        defaultValues: {
            school: activeSchoolOrCompany?.["@id"] ?? "",
            company: CompanyApi.toResourceIRI(formContainerState.entityId),
            user: UserApi.toResourceIRI(user?.id),
        },
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = async (formData: Entity) => {
        setLoading(true);
        EntityApi.postCollection<
            {
                success: boolean;
            },
            Entity
        >(formData)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    if (response.success) {
                        toast({
                            title: t(
                                "padm.PartnershipCompanyForm:toast.success.partnership"
                            ),
                            status: "success",
                        });
                        reset();
                        formContainerState.close();
                    } else {
                        toast({
                            title: t(
                                "padm.PartnershipCompanyForm:toast.fail.partnership"
                            ),
                            status: "error",
                        });
                    }
                }
            })
            .finally(() => setLoading(false));
    };

    const loadUserOptions = () => {
        setLoading(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            // userType: USERTYPE.USERTYPE_INTERNSHIP_COORDINATOR,
            userType: [
                USERTYPE.USERTYPE_INTERNSHIP_COORDINATOR,
                USERTYPE.USERTYPE_SCHOOL_AND_COMPANY,
            ],
            ["companies.id"]: CompanyApi.toResourceIRI(
                formContainerState.entityId
            ),
        };
        UserApi.getCollectionCoordinator<User>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    setExistinginternshipCoordinator(response?.items);
                }
            })
            .finally(() => setLoading(false));
    };

    // useEffect
    useEffect(() => {
        if (formContainerState.entityId) {
            loadUserOptions();
        }
    }, [formContainerState.entityId]);

    // render
    const renderForm = () => {
        return (
            <form
                id="app-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                {isFormOpen ? (
                    <VStack gap={5} alignItems={"stretch"}>
                        <Stack
                            gap={5}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <AppFormControl
                                label={t("ent.User:firstName.label")}
                                isInvalid={!!errors?.firstName}
                                message={errors?.firstName?.message}
                                isLoading={loading}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.User:firstName.placeholder"
                                    )}
                                    {...register("firstName")}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.User:lastName.label")}
                                isInvalid={!!errors?.lastName}
                                message={errors?.lastName?.message}
                                isLoading={loading}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.User:lastName.placeholder"
                                    )}
                                    {...register("lastName")}
                                />
                            </AppFormControl>
                        </Stack>
                        <Stack
                            gap={5}
                            direction={{
                                base: "column",
                                sm: "column",
                                md: "row",
                            }}
                        >
                            <AppFormControl
                                label={t("ent.User:email.label")}
                                isInvalid={!!errors?.email}
                                message={errors?.email?.message}
                                isLoading={loading}
                            >
                                <Input
                                    type="email"
                                    placeholder={t(
                                        "ent.User:email.placeholder"
                                    )}
                                    {...register("email")}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.User:phone.label")}
                                isLoading={loading}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.User:phone.placeholder"
                                    )}
                                    {...register("phone")}
                                />
                            </AppFormControl>
                        </Stack>
                    </VStack>
                ) : (
                    <></>
                )}
            </form>
        );
    };

    return (
        <AppFormContainer
            title={t("padm.PartnershipCompanyForm:text.pageTitle")}
            state={formContainerState}
            type={"modal"}
            size={"xl"}
            isFooter={true}
            submitLabel={t("padm.PartnershipCompanyForm:modal.submit.label")}
        >
            <>
                <VStack gap={5} alignItems={"stretch"}>
                    <Text textStyle={"mediumLg"}>
                        {t("padm.PartnershipCompanyForm:modal.heading")}{" "}
                        <Text
                            as={"span"}
                            fontWeight={700}
                            color={"var(--chakra-colors-primary)"}
                            textDecoration={"underline"}
                        >
                            {data?.name}
                        </Text>
                    </Text>
                    {existinginternshipCoordinator.length > 0 && (
                        <Box>
                            <Text textStyle={"mediumMd"} fontWeight={700}>
                                {t(
                                    "padm.PartnershipCompanyForm:modal.existingCoordinator"
                                )}
                            </Text>
                            <SimpleGrid
                                columns={{ sm: 1, md: 2 }}
                                spacingX={5}
                                spacingY={3}
                                pt={4}
                                w={"full"}
                            >
                                {existinginternshipCoordinator.map(
                                    (user, index) => (
                                        <Flex
                                            key={index}
                                            alignItems={"center"}
                                            boxShadow={"base"}
                                            bg={"var(--chakra-colors-white)"}
                                            border={
                                                "1px solid var(--chakra-colors-primaryT80)"
                                            }
                                            borderRadius={"0.25rem"}
                                            h={"50px"}
                                            px={3}
                                            gap={3}
                                        >
                                            <Avatar
                                                size={"sm"}
                                                name={user.fullName}
                                                color={
                                                    "var(--chakra-colors-primaryS10)"
                                                }
                                                bgColor={
                                                    "var(--chakra-colors-primaryT80)"
                                                }
                                            />
                                            <Text
                                                noOfLines={1}
                                                textStyle={"mediumSm"}
                                            >
                                                {user.fullName}
                                            </Text>
                                        </Flex>
                                    )
                                )}
                            </SimpleGrid>
                        </Box>
                    )}
                    <Text>
                        <Text as={"span"} fontWeight={700}>
                            {t(
                                "padm.PartnershipCompanyForm:modal.instructions"
                            )}{" "}
                        </Text>{" "}
                        {t("padm.PartnershipCompanyForm:modal.note")}
                    </Text>
                    <Checkbox onChange={(e) => setIsFormOpen(e.target.checked)}>
                        {t("padm.PartnershipCompanyForm:modal.checkbox")}
                    </Checkbox>
                    {renderForm()}
                </VStack>
            </>
        </AppFormContainer>
    );
};
